import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ThemeProvider,
  Stepper,
  Step,
  StepButton,
  Grid,
  createTheme,
} from "@mui/material";
import "./misdeliveryBOL.css";
import { REQUIRED_MSG } from "../../schema/formik";

import {
  misdeliveryBolInitialValues,
  useMisdeliveryBolForm,
} from "../../@hooks/useMisdeliveryBolForm";
import type { MisdeliveryBolFormValues } from "../../@hooks/useMisdeliveryBolForm";
import { ErrorMessages, THEME } from "../../constants";
import PickupInformationStep from "./components/PickupInformationStep";
import DriverPickupInformationStep from "./components/DriverPickupInformationStep";
import { GetPickupDetailsAPI } from "../../API/getPickupDetails";
import { CreateMisdeliveryBol } from "../../API/CreateMisdeliveryBol";
import { UpdateMisdeliveryBol } from "../../API/UpdateMisdeliveryBol";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import Loader from "../../components/loader/loader";
import { MisDeliveryStatus } from "../../models";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import { toOptionalNumber, toOptionalString, compareObjects } from "../../helpers";
import { hasMisdeliveryHazmatApprovalPermissionSelector } from "../../AppState/selectors/userPermissionsSelectors";

/**
 * Performance Optimizations:
 * 1. Memoized stepper component to prevent unnecessary re-renders
 * 2. Stable callbacks with proper dependency arrays
 * 3. Memoized computed values (form disabled state, theme)
 * 4. Optimized validation logic to avoid redundant operations
 * 5. Child components memoized to isolate re-renders
 */

interface MisdeliveryBOLProps {
  misdeliveryEntry?: Partial<MisdeliveryBolFormValues>;
  osdNumber?: number | string;
  investigationStatus?: MisDeliveryStatus;
  bol?: Partial<MisdeliveryBolFormValues> | null;
}

const buildBasePayload = (values: any, isHazmat: boolean) => ({
  pickupNumber: toOptionalNumber(values.pickupNumber),
  pickupBusinessName: toOptionalString(values.pickupBusinessName),
  pickupAddress1: toOptionalString(values.pickupAddress1),
  pickupAddress2: toOptionalString(values.pickupAddress2),
  pickupCity: toOptionalString(values.pickupCity),
  pickupState: toOptionalString(values.pickupState),
  pickupZip: toOptionalString(values.pickupZip),
  pickupPhoneNumber: toOptionalNumber(values.pickupPhoneNumber),
  pickupContactName: toOptionalString(values.pickupContactName),
  freightDescription: toOptionalString(values.freightDescription),
  isMisdeliveryHazmat: toOptionalString(values.isMisdeliveryHazmat),
  totalPiecesMisdelivered: toOptionalNumber(values.totalPiecesMisdelivered),
  weight: toOptionalNumber(values.weight),
  ...(isHazmat
    ? {
        unNumber: toOptionalString(values.unNumber),
        unNumberSequence: toOptionalNumber(values.unNumberSequence) ?? 0,
      }
    : {
        unNumber: undefined,
        unNumberSequence: undefined,
      }),
});

const getMisdeliveredBolPro = (value: any) => {
  const bolPro = toOptionalString(value);

  if (bolPro?.length === 12 && /^\d{12}$/.test(bolPro)) {
    return toOptionalNumber(bolPro.slice(0, 11));
  }

  return toOptionalNumber(value);
};

const buildPayload = (values: any, isHazmat: boolean) => {
  const base = buildBasePayload(values, isHazmat);

  return {
    ...base,
    proNumber: toOptionalNumber(values.proNumber),
    misdeliveredPalletPro: toOptionalNumber(values.misdeliveredPalletPro),
    terminalId: toOptionalString(values.terminalId),
    consigneeName: toOptionalString(values.consigneeName),
    consigneeAddress1: toOptionalString(values.consigneeAddress1),
    consigneeAddress2: toOptionalString(values.consigneeAddress2),
    consigneeCity: toOptionalString(values.consigneeCity),
    consigneeState: toOptionalString(values.consigneeState),
    consigneeZip: toOptionalString(values.consigneeZip),
    misdeliveredBolPro: getMisdeliveredBolPro(values.misdeliveredBolPro),
  };
};

// Constants for misdelivery BOL stepper
const MISDELIVERY_BOL_STEPS = [
  "PICKUP INFORMATION",
  "DRIVER PICKUP INFORMATION",
];

const PICKUP_STEP_INDEX = 0;
const DRIVER_PICKUP_STEP_INDEX = 1;

const MisdeliveryBOLStepper = React.memo(function MisdeliveryBOLStepper({
  activeStep,
  onStepClick,
  forceCompleteAll,
}: {
  activeStep: number;
  onStepClick: (step: number) => void | Promise<void>;
  forceCompleteAll?: boolean;
}) {
  return (
    <Stepper
      data-testid="misdelivery-bol-stepper"
      nonLinear
      activeStep={activeStep}
    >
      {MISDELIVERY_BOL_STEPS.map((label, index) => {
        let className = "";
        if (forceCompleteAll || index < activeStep) {
          className = "completeStep";
        } else if (index === activeStep) {
          className = "activeStep";
        }

        return (
          <Step
            key={label}
            className={className}
            data-testid={`misdelivery-bol-step-container-${index}`}
          >
            <StepButton
              data-testid={`misdelivery-bol-step-${index}`}
              onClick={() => onStepClick(index)}
              disableRipple
            >
              {label}
            </StepButton>
          </Step>
        );
      })}
    </Stepper>
  );
});

const MisdeliveryBOL: React.FC<MisdeliveryBOLProps> = ({
  misdeliveryEntry,
  osdNumber,
  investigationStatus,
  bol,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
   const hasApprovalPermission = useSelector(
    hasMisdeliveryHazmatApprovalPermissionSelector,
  );
  const updateModeSnapshotRef = useRef<MisdeliveryBolFormValues | null>(null);
  const baselineSnapshotRef = useRef<MisdeliveryBolFormValues | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  // Memoize conversion functions to maintain stable references

  const handleCreate = async (payload: any) => {
    setLoading(true);
    await CreateMisdeliveryBol(osdNumber ?? 0, payload);
  };

  const validateBeforeUpdate = async (values: any): Promise<boolean> => {
    if (bol?.pickupNumber && !values.pickupNumber) return false;

    if (!values.pickupNumber) return true;

    const res = await GetPickupDetailsAPI(values.pickupNumber);
    return !isEmptyResponse(res);
  };

  const handleUpdate = async (
    values: any,
    updatePayload: any,
    isHazmat: boolean,
  ) => {
    setLoading(true);

    const updateBaseline = updateModeSnapshotRef.current;
    const hasChanges =
      !updateBaseline || !compareObjects(values, updateBaseline);

    if (hasChanges || isHazMatPanding) {
      await UpdateMisdeliveryBol(osdNumber ?? 0, updatePayload);
    }
  };

  const getSuccessMessage = () =>
    isExceptionEntry && investigationStatus === MisDeliveryStatus.hazmatPending
      ? "BOL generated successfully."
      : "BOL regenerated successfully.";

  const handleSuccess = () => {
    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.SUCCESS,
        getSuccessMessage(),
        Date.now().toString(),
      ),
    );

    const osdNumberStr = osdNumber?.toString().trim();
    if (osdNumberStr) {
      navigate(
        `/misdelivery/bol/${encodeURIComponent(osdNumberStr)}/send-email`,
      );
    }
  };

  const handleError = (error: any) => {
    const message =
      typeof error === "string"
        ? error
        : ErrorMessages.misdeliveryBolCreateError;

    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        message,
        Date.now().toString(),
      ),
    );
  };

  const { formik } = useMisdeliveryBolForm(misdeliveryEntry, {
    onSubmit: async (values) => {
      const isHazmat = values.isMisdeliveryHazmat === "Y";

      const payload = buildPayload(values, isHazmat);
      const updatePayload = buildBasePayload(values, isHazmat);

      try {
        if (isExceptionEntry) {
          await handleCreate(payload);
        } else {
          const shouldContinue = await validateBeforeUpdate(values);
          if (!shouldContinue) return;

          await handleUpdate(values, updatePayload, isHazmat);
        }

        handleSuccess();
      } catch (error: any) {
        handleError(error);
      } finally {
        setLoading(false);
        dispatch(isFormValueChanged(false));
      }
    },
  });

  // Initialize baseline snapshot for create mode (when misdeliveryEntry is not provided).
  // This enables accurate isFormValueChanged tracking as the user edits the form.
  useEffect(() => {
    if (misdeliveryEntry || baselineSnapshotRef.current) return;
    baselineSnapshotRef.current = structuredClone(formik.values);
    dispatch(isFormValueChanged(false));
  }, [dispatch, formik.values, misdeliveryEntry]);

  useEffect(() => {
    if (!misdeliveryEntry || formik.dirty) return;

    // Ensure values (like terminalId: "ABL") patch in even if data arrives after mount.
    const nextValues: MisdeliveryBolFormValues = {
      ...misdeliveryBolInitialValues,
      ...formik.values,
      ...misdeliveryEntry,
      terminalId:
        (misdeliveryEntry as any)?.terminalId?.toString() ??
        formik?.values?.terminalId,
    };

    formik.setValues(nextValues, false);
    baselineSnapshotRef.current = nextValues;
    dispatch(isFormValueChanged(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [misdeliveryEntry]);

  // Continuously track form changes and update Redux state
  useEffect(() => {
    const lastCreatedValues = isUpdateMode
      ? updateModeSnapshotRef.current
      : baselineSnapshotRef.current;
    if (!lastCreatedValues) return;

    const formData = formik.values as any;
    const hasChanged = !compareObjects(formData, lastCreatedValues as any);
    dispatch(isFormValueChanged(hasChanged));
  }, [dispatch, formik.values, isUpdateMode]);

  const pickupLookupCacheRef = useRef<{
    pickupNumber: string;
    isValid: boolean;
  } | null>(null);

  // Memoize form state flags to prevent child re-renders
  const isExceptionEntry = useMemo(
    () =>
      investigationStatus == null ||
      investigationStatus === MisDeliveryStatus.exceptionEntry,
    [investigationStatus],
  );

  const investigationComplete = useMemo(
    () =>
      [MisDeliveryStatus.investigationComplete].includes(
        investigationStatus as MisDeliveryStatus,
      ),
    [investigationStatus],
  );

  const formFieldsDisabled = useMemo(
    () => !isExceptionEntry,
    [isExceptionEntry],
  );

  const currentMisdeliveredBolPro = formik.values.misdeliveredBolPro;

  const hasExistingBol = useMemo(() => {
    const value =
      (misdeliveryEntry as any)?.misdeliveredBolPro ??
      currentMisdeliveredBolPro;
    return value != null && value.toString().trim().length > 0;
  }, [currentMisdeliveredBolPro, misdeliveryEntry]);

  const showUpdateButton = useMemo(
    () => formFieldsDisabled && hasExistingBol && !isUpdateMode,
    [formFieldsDisabled, hasExistingBol, isUpdateMode],
  );

  const isHazMatPanding = useMemo(
    () => investigationStatus === MisDeliveryStatus.hazmatPending,
    [investigationStatus],
  );

  // Effective disabled state for the BOL form (update mode overrides view-only state)
  const effectiveFormDisabled = useMemo(
    () => formFieldsDisabled && !isUpdateMode,
    [formFieldsDisabled, isUpdateMode],
  );

  const theme = useMemo(() => createTheme(THEME), []);

  const focusField = useCallback((fieldName: string) => {
    // Defer to allow validation/error state to render
    globalThis.window.setTimeout(() => {
      const el =
        document.getElementsByName(fieldName)?.[0] ??
        document.getElementById(fieldName) ??
        document.querySelector(
          `[name="${CSS?.escape ? CSS.escape(fieldName) : fieldName}"]`,
        );
      (el as any)?.focus?.();
    }, 0);
  }, []);

  const handleUpdateClick = useCallback(() => {
    updateModeSnapshotRef.current = structuredClone(formik.values);
    setIsUpdateMode(true);
    dispatch(isFormValueChanged(false));
    setActiveStep(PICKUP_STEP_INDEX);
    focusField("pickupNumber");
  }, [dispatch, focusField, formik.values]);

  const handleCancelUpdateClick = useCallback(() => {
    const snapshot = updateModeSnapshotRef.current;

    if (snapshot) {
      formik.resetForm({ values: snapshot });
    }

    updateModeSnapshotRef.current = null;
    setIsUpdateMode(false);
    setActiveStep(PICKUP_STEP_INDEX);
    dispatch(isFormValueChanged(false));
  }, [dispatch, formik]);

  const handleGenerateBolClick = useCallback(() => {
    // Navigate to send-email without calling create/update APIs.
    const osdNumberStr = osdNumber?.toString().trim() ?? "";
    if (!osdNumberStr) return;

    navigate(`/misdelivery/bol/${encodeURIComponent(osdNumberStr)}/send-email`);
  }, [navigate, osdNumber]);

  const clearPickupDetails = useCallback(() => {
    formik.setFieldValue("pickupBusinessName", "", false);
    formik.setFieldValue("pickupAddress1", "", false);
    formik.setFieldValue("pickupAddress2", "", false);
    formik.setFieldValue("pickupCity", "", false);
    formik.setFieldValue("pickupState", "", false);
    formik.setFieldValue("pickupZip", "", false);
    formik.setFieldValue("pickupPhoneNumber", "", false);
    formik.setFieldValue("pickupContactName", "", false);
  }, [formik]);

  // Helper: Check if API response is empty
  const isEmptyResponse = (res: any): boolean => {
    return (
      !res ||
      (typeof res === "object" &&
        !Array.isArray(res) &&
        Object.keys(res).length === 0)
    );
  };

  // Helper: Populate formik with pickup details
  const populatePickupDetails = useCallback(
    (res: any) => {
      formik.setFieldValue(
        "pickupBusinessName",
        res.pickupBusinessName ?? "",
        false,
      );
      formik.setFieldValue("pickupAddress1", res.pickupAddress1 ?? "", false);
      formik.setFieldValue("pickupAddress2", res.pickupAddress2 ?? "", false);
      formik.setFieldValue("pickupCity", res.pickupCity ?? "", false);
      formik.setFieldValue("pickupState", res.pickupState ?? "", false);
      formik.setFieldValue("pickupZip", res.pickupZip ?? "", false);
      formik.setFieldValue(
        "pickupPhoneNumber",
        res?.pickupPhoneNumber?.toString() ?? "",
        false,
      );
      formik.setFieldValue(
        "pickupContactName",
        res.pickupContactName ?? "",
        false,
      );
      formik.setFieldError("pickupNumber", undefined as any);
    },
    [formik],
  );

  // Helper: Handle invalid pickup
  const handleInvalidPickup = useCallback(
    (pickupNumber: string, errorMessage: string) => {
      pickupLookupCacheRef.current = { pickupNumber, isValid: false };
      clearPickupDetails();
      formik.setTouched(
        {
          ...(formik.touched as any),
          pickupNumber: true,
        },
        false,
      );
      formik.setErrors({
        ...(formik.errors as any),
        pickupNumber: errorMessage,
      });
    },
    [clearPickupDetails, formik],
  );

  const fetchAndPatchPickupDetails = useCallback(
    async (pickupNumberRaw: number): Promise<boolean> => {
      const pickupNumber = (pickupNumberRaw ?? "").toString().trim();
      if (!pickupNumber) return false;

      if (pickupLookupCacheRef.current?.pickupNumber === pickupNumber) {
        if (!pickupLookupCacheRef.current.isValid) {
          formik.setFieldTouched("pickupNumber", true, false);
          formik.setFieldError("pickupNumber", "Invalid pickup number");
        }
        return pickupLookupCacheRef.current.isValid;
      }

      try {
        const res = await GetPickupDetailsAPI(pickupNumber);

        if (isEmptyResponse(res)) {
          handleInvalidPickup(pickupNumber, ErrorMessages.invalidPickupNumber);
          return false;
        }

        pickupLookupCacheRef.current = { pickupNumber, isValid: true };
        populatePickupDetails(res as any);
        return true;
      } catch (error: unknown) {
        const message =
          typeof error === "string" ? error : ErrorMessages.invalidPickupNumber;
        handleInvalidPickup(pickupNumber, message);
        return false;
      }
    },
    [formik, handleInvalidPickup, populatePickupDetails],
  );

  const validatePickupNumberIfProvidedBeforeNext =
    useCallback(async (): Promise<boolean> => {
      const pickupNumberText = ((formik.values as any).pickupNumber ?? "")
        .toString()
        .trim();

      // Treat 0 as "not provided" to avoid accidental lookups from an empty numeric field.
      if (!pickupNumberText || pickupNumberText === "0") {
        return true;
      }

      formik.setFieldTouched("pickupNumber", true, false);

      const pickupNumberNumeric = Number(pickupNumberText);
      if (!Number.isFinite(pickupNumberNumeric) || pickupNumberNumeric <= 0) {
        formik.setFieldError(
          "pickupNumber",
          "Misdelivery Pickup Number is invalid",
        );
        focusField("pickupNumber");
        return false;
      }

      const ok = await fetchAndPatchPickupDetails(pickupNumberNumeric);
      if (!ok) {
        const existing = (formik.errors as any)?.pickupNumber;
        const message =
          typeof existing === "string" && existing.trim().length
            ? existing
            : ErrorMessages.invalidPickupNumber;

        formik.setTouched(
          {
            ...(formik.touched as any),
            pickupNumber: true,
          },
          false,
        );
        formik.setErrors({
          ...(formik.errors as any),
          pickupNumber: message,
        });
        focusField("pickupNumber");
      }
      return ok;
    }, [fetchAndPatchPickupDetails, focusField, formik]);

  const validatePickupStepBeforeNext =
    useCallback(async (): Promise<boolean> => {
      const values = formik.values as any;

      const pickupNumberText = (values.pickupNumber ?? "").toString().trim();
      const isManualPickupDetailsEntry =
        !pickupNumberText || pickupNumberText === "0";

      const pickupFields: Array<keyof MisdeliveryBolFormValues> = [
        "freightDescription",
        "isMisdeliveryHazmat",
        "unNumber",
        "pickupBusinessName",
        "pickupAddress1",
        "pickupCity",
        "pickupZip",
        "totalPiecesMisdelivered",
        "weight",
      ];

      // Mark fields touched
      pickupFields.forEach((f) => {
        void formik.setFieldTouched(f, true, false); // NOSONAR
      });

      const errors: Partial<Record<keyof MisdeliveryBolFormValues, string>> =
        {};

      const isBlank = (val: unknown): boolean =>
        val == null || (typeof val === "string" && val.trim() === "");

      const addError = (
        field: keyof MisdeliveryBolFormValues,
        condition: boolean,
        message: string = REQUIRED_MSG,
      ) => {
        if (condition) errors[field] = message;
      };

      // Pickup number validation
      const pickupNumberOk = await validatePickupNumberIfProvidedBeforeNext();
      if (!pickupNumberOk) {
        errors.pickupNumber =
          (formik.errors as any)?.pickupNumber ??
          ErrorMessages.invalidPickupNumber;
      }

      // Common validations
      addError("freightDescription", isBlank(values.freightDescription));
      addError("isMisdeliveryHazmat", isBlank(values.isMisdeliveryHazmat));

      // Conditional validations
      addError(
        "unNumber",
        values.isMisdeliveryHazmat === "Y" && isBlank(values.unNumber),
      );

      if (isManualPickupDetailsEntry) {
        [
          "pickupBusinessName",
          "pickupAddress1",
          "pickupCity",
          "pickupZip",
        ].forEach((field) =>
          addError(
            field as keyof MisdeliveryBolFormValues,
            isBlank(values[field]),
          ),
        );
      }

      addError(
        "totalPiecesMisdelivered",
        isBlank(values.totalPiecesMisdelivered?.toString()),
      );

      addError("weight", isBlank(values.weight?.toString()));

      // Reset previous errors
      pickupFields.forEach((f) => formik.setFieldError(f, undefined as any));
      if (!errors.pickupNumber) {
        formik.setFieldError("pickupNumber", undefined as any);
      }

      // Apply new errors
      Object.entries(errors).forEach(([field, message]) => {
        const key = field as keyof MisdeliveryBolFormValues;
        formik.setFieldError(key, message);
        formik.setFieldTouched(key, true, false);
      });

      const firstInvalid = Object.keys(errors)[0] as
        | keyof MisdeliveryBolFormValues
        | undefined;

      if (firstInvalid) {
        focusField(firstInvalid);
        return false;
      }

      return true;
    }, [focusField, formik, validatePickupNumberIfProvidedBeforeNext]);
  const goToStep = useCallback(
    async (nextStep: number) => {
      // Only validate when moving forward to driver step in editable mode
      if (!effectiveFormDisabled && nextStep === DRIVER_PICKUP_STEP_INDEX) {
        const isValid = await validatePickupStepBeforeNext();
        if (!isValid) return;
      }
      setActiveStep(nextStep);
    },
    [effectiveFormDisabled, validatePickupStepBeforeNext],
  );

  const handleStepClick = useCallback(
    async (step: number) => {
      await goToStep(step);
    },
    [goToStep],
  );

  // Memoize navigation handlers
  const goToPickupStep = useCallback(() => {
    void goToStep(PICKUP_STEP_INDEX);
  }, [goToStep]);

  const goToDriverPickupStep = useCallback(() => {
    void goToStep(DRIVER_PICKUP_STEP_INDEX);
  }, [goToStep]);

  const handleSubmitWithValidation = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const errors = await formik.validateForm();

      const allFieldsTouched = Object.keys(formik.values).reduce(
        (touched, field) => {
          touched[field as keyof MisdeliveryBolFormValues] = true;
          return touched;
        },
        {} as Record<keyof MisdeliveryBolFormValues, boolean>,
      );

      formik.setTouched(allFieldsTouched, false);

      if (Object.keys(errors).length > 0) {
        formik.setErrors(errors);

        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) {
          const el =
            document.getElementsByName(firstErrorField)?.[0] ??
            document.getElementById(firstErrorField) ??
            document.querySelector(
              `[name="${CSS?.escape ? CSS.escape(firstErrorField) : firstErrorField}"]`,
            );
          el?.focus?.();
        }
        return;
      }

      await formik.submitForm();
    },
    [formik],
  );

  return (
    <div className="misdelivery-bol-container">
      <form onSubmit={handleSubmitWithValidation} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ThemeProvider theme={theme}>
              <MisdeliveryBOLStepper
                activeStep={activeStep}
                onStepClick={handleStepClick}
                forceCompleteAll={
                  effectiveFormDisabled || investigationComplete || isUpdateMode
                }
              />
            </ThemeProvider>
          </Grid>

          <Grid item xs={12}>
            {/* Step components receive minimal, stable props for optimal re-render performance */}
            {activeStep === PICKUP_STEP_INDEX ? (
              <PickupInformationStep
                formik={formik}
                onNext={goToDriverPickupStep}
                bol={bol}
                formFieldsDisabled={effectiveFormDisabled}
                showUpdateButton={showUpdateButton}
                hideEditButton={investigationComplete}
                showCancelButton={isUpdateMode}
                onUpdateClick={handleUpdateClick}
                onCancelClick={handleCancelUpdateClick}
                onGenerateBolClick={handleGenerateBolClick}
                isHazMatPanding={isHazMatPanding}
              />
            ) : (
              <DriverPickupInformationStep
                formik={formik}
                onBack={goToPickupStep}
                formFieldsDisabled={effectiveFormDisabled || isUpdateMode}
                terminalReadOnly={isUpdateMode}
                bolProReadOnly={isUpdateMode}
                submitButtonText={
                  bol?.isMisdeliveryHazmat !== "Y" &&
                  formik.values.isMisdeliveryHazmat === "Y" && !hasApprovalPermission
                    ? "Manager Approval"
                    : "Submit & Generate BOL"
                }
              />
            )}
          </Grid>
        </Grid>
      </form>
      <Loader pageLoader={loading} />
    </div>
  );
};

export default MisdeliveryBOL;
